/**
 * Pembungkus IndexedDB seperlunya.
 *
 * Sengaja tanpa pustaka luar. Yang dibutuhkan kasir hanya empat operasi
 * (baca, tulis, hapus, daftar) pada dua penyimpanan, dan basis kode yang
 * kecil lebih mudah ditelusuri saat ada yang aneh di perangkat pelanggan
 * daripada lapisan abstraksi pihak ketiga.
 */

const NAMA_DB = 'santap-kasir';
const VERSI = 1;

export const TOKO = {
  katalog: 'katalog',   // katalog per outlet, untuk kerja offline
  outbox: 'outbox',     // pesanan & pembayaran yang menunggu terkirim
  meta: 'meta',         // sesi, nomor urut struk, penanda lain-lain
} as const;

let dbP: Promise<IDBDatabase> | null = null;

function buka(): Promise<IDBDatabase> {
  if (dbP) return dbP;
  dbP = new Promise((resolve, reject) => {
    const req = indexedDB.open(NAMA_DB, VERSI);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TOKO.katalog)) db.createObjectStore(TOKO.katalog);
      if (!db.objectStoreNames.contains(TOKO.meta)) db.createObjectStore(TOKO.meta);
      if (!db.objectStoreNames.contains(TOKO.outbox)) {
        const s = db.createObjectStore(TOKO.outbox, { keyPath: 'id' });
        s.createIndex('urut', 'dibuatPada');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbP;
}

function jalankan<T>(
  toko: string,
  mode: IDBTransactionMode,
  kerja: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return buka().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(toko, mode);
        const req = kerja(tx.objectStore(toko));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const idb = {
  get: <T>(toko: string, kunci: IDBValidKey) =>
    jalankan<T | undefined>(toko, 'readonly', (s) => s.get(kunci) as IDBRequest<T | undefined>),

  set: (toko: string, nilai: unknown, kunci?: IDBValidKey) =>
    jalankan(toko, 'readwrite', (s) =>
      kunci === undefined ? s.put(nilai) : s.put(nilai, kunci),
    ),

  del: (toko: string, kunci: IDBValidKey) =>
    jalankan(toko, 'readwrite', (s) => s.delete(kunci)),

  semua: <T>(toko: string) =>
    jalankan<T[]>(toko, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),

  jumlah: (toko: string) =>
    jalankan<number>(toko, 'readonly', (s) => s.count()),
};

/**
 * Deteksi apakah penyimpanan bisa dipakai sama sekali.
 *
 * Di jendela penyamaran atau saat data situs diblokir, IndexedDB bisa gagal
 * dibuka. Kasir harus tahu ini SEBELUM shift dimulai — bukan saat internet
 * mati dan ternyata tidak ada tempat menyimpan pesanan.
 */
export async function penyimpananSiap(): Promise<boolean> {
  try {
    await idb.set(TOKO.meta, Date.now(), '__uji__');
    await idb.del(TOKO.meta, '__uji__');
    return true;
  } catch {
    return false;
  }
}
